import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProgress } from '../context/ProgressContext';
import { getSessionId } from '../utils/session';
import toast from 'react-hot-toast';
import Quiz from '../components/Quiz';

export default function LearnModule() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { fetchProgress, modulesCompleted } = useProgress();
  const navigate = useNavigate();

  const isCurious = 
    localStorage.getItem('curious_status') === 'active' ||
    localStorage.getItem('curious_started') === 'true';

  const isCuriousPlus = 
    localStorage.getItem('curious_status') === 'active' &&
    localStorage.getItem('curious_email') !== null;  

  const [module, setModule] = useState(null);
  const [progress, setProgress] = useState(null);
  const [currentScreen, setCurrentScreen] = useState(0);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [startTime] = useState(Date.now());
  const [quizAnswers, setQuizAnswers] = useState({});
  const [isUserLoading, setIsUserLoading] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [emailForReminder, setEmailForReminder] = useState('');
  const [reminderSent, setReminderSent] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [sharing, setSharing] = useState(false);
  const [shareSent, setShareSent] = useState(false);
  const [shareMethod, setShareMethod] = useState('link');
  const [courseInfo, setCourseInfo] = useState(null);
  const [courseProgress, setCourseProgress] = useState(null);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [showCompletionOptions, setShowCompletionOptions] = useState(false);

  useEffect(() => {
    if (user !== undefined) {
      setIsUserLoading(false);
    }
  }, [user]);

  useEffect(() => {
    setCompleted(false);
    setCurrentScreen(0);
    setQuizAnswers({});
    setShowResults(false);
    setIsReviewMode(false);
    setShowCompletionOptions(false);
    fetchModule();
  }, [slug]);

  useEffect(() => {
    if (showShareModal && shareMethod === 'link' && !shareUrl) {
      generateShareLink();
    }
  }, [showShareModal, shareMethod]);

  const fetchModule = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/education-modules/slug/${slug}`);
      if (!response.ok) {
        toast.error('Module not found');
        navigate('/');
        return;
      }
      const data = await response.json();

      if (!data.screens || !Array.isArray(data.screens)) {
        data.screens = [];
      }

      setModule(data);

      if (data.courseId) {
        await fetchCourseInfo(data.courseId);
      }

      const progressRes = await fetch(`/api/education/progress/${data._id}`, {
        headers: {
          'X-Session-Id': getSessionId()
        }
      });
      if (progressRes.ok) {
        const progressData = await progressRes.json();
        setProgress(progressData);
        if (progressData.completed) {
          setCompleted(true);
          setIsReviewMode(true);
          setCurrentScreen(0);
          setShowResults(true);

          if (progressData.quizAnswers) {
            setQuizAnswers(progressData.quizAnswers);
          }
          toast.success(`📚 You've already completed this module with a score of ${progressData.score || 0}%`);
        } else {
          const maxScreen = data.screens ? data.screens.length : 0;
          const safeScreen = progressData.currentScreen || 0;
          setCurrentScreen(safeScreen > maxScreen ? maxScreen : safeScreen);
        }
      }

    } catch (error) {
      console.error('Error fetching module:', error);
      toast.error('Failed to load module');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourseInfo = async (courseId) => {
    try {
      const res = await fetch(`/api/education-modules/course/${courseId}`, {
        headers: {
          'X-Session-Id': getSessionId()
        }
      });
      if (res.ok) {
        const data = await res.json();
        setCourseInfo(data);

        const progressRes = await fetch(`/api/education/course-progress/${courseId}`, {
          headers: {
            'X-Session-Id': getSessionId()
          }
        });
        if (progressRes.ok) {
          const progressData = await progressRes.json();
          setCourseProgress(progressData);
        }
      }
    } catch (error) {
      console.error('Error fetching course info:', error);
    }
  };

const trackAction = async (action, data = {}) => {
  if (!module || isReviewMode) return;
  
  try {
    const token = localStorage.getItem('token');
    const sessionId = getSessionId();
    const headers = {
      'Content-Type': 'application/json',
      'X-Session-Id': sessionId
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    console.log('📤 Sending progress:', { moduleId: module._id, action, sessionId });
    
    // ✅ Fix: Assign the response to a variable
    const response = await fetch('/api/education/progress', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        moduleId: module._id,
        action,
        ...data
      })
    });
    
    // ✅ Read the response
    const result = await response.json();
    console.log('📥 Progress response:', result);
    
    if (!response.ok) {
      console.error('❌ Progress tracking failed:', result);
    }
  } catch (error) {
    console.error('Error tracking progress:', error);
  }
};

  const generateShareLink = async () => {
    setSharing(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`/api/education-modules/${module._id}/share`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          shareMethod: 'link'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setShareUrl(data.shareUrl);
      } else {
        toast.error('Failed to generate share link');
      }
    } catch (error) {
      console.error('Error generating share link:', error);
      toast.error('Failed to generate share link');
    } finally {
      setSharing(false);
    }
  };

const handleStart = async () => {
    if (isReviewMode) {
      setCurrentScreen(1);
      return;
    }
  // ✅ Set Curious status for anonymous users who start a module
  if (!user) {
    localStorage.setItem('curious_started', 'true');
    localStorage.setItem('curious_status', 'active');
    await fetchProgress();
    window.dispatchEvent(new Event('curiousStatusChanged'));
  }
  
  trackAction('start');
  setCurrentScreen(1);
  };

  const handleScreenChange = (index) => {
    const screens = module.screens || [];
    const targetScreenIndex = index - 1;
    if (index < currentScreen && targetScreenIndex >= 0) {
      const targetScreen = screens[targetScreenIndex];
      if (targetScreen && targetScreen.type === 'quiz') {
        if (quizAnswers[targetScreenIndex] !== undefined) {
          toast('You cannot change a quiz answer once answered.', { icon: '🔒' });
          return;
        }
      }
    }

    if (isReviewMode) {
      setCurrentScreen(index);
      return;
    }
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    trackAction('screen', { screen: index });
    trackAction('time', { timeSpent });
    setCurrentScreen(index);
  };

  const handleQuizAnswer = (screenIndex, answerIndex) => {
    if (isReviewMode) return;
    setQuizAnswers(prev => ({ ...prev, [screenIndex]: answerIndex }));
  };

  const handleSendReminder = async () => {
    if (!emailForReminder) return;

    try {
      const response = await fetch('/api/session/send-reminder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': getSessionId()
        },
        body: JSON.stringify({ email: emailForReminder })
      });
      if (response.ok) {
        setReminderSent(true);
        localStorage.setItem('curious_email', emailForReminder);
        localStorage.setItem('curious_status', 'active');
        toast.success('✅ Email sent! Check your inbox.');
        setTimeout(() => window.location.reload(), 1500);
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to send email. Please try again.');
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error('Failed to send email.');
    }
  };

const handleComplete = async () => {
  if (isReviewMode) return;

  const timeSpent = Math.floor((Date.now() - startTime) / 1000);
  trackAction('time', { timeSpent });

  const screens = module.screens || [];
  const quizScreens = screens.filter(s => s && s.type === 'quiz');
  let score = 0;
  let totalQuestions = 0;
  let correctAnswers = 0;
  let savedAnswers = {};

  if (quizScreens.length > 0) {
    quizScreens.forEach((screen) => {
      const screenIndex = screens.indexOf(screen);
      const userAnswer = quizAnswers[screenIndex];
      const correctIndex = screen.quizCorrectAnswer !== undefined && screen.quizCorrectAnswer !== null
        ? screen.quizCorrectAnswer - 1
        : 0;
      savedAnswers[screenIndex] = userAnswer;
      if (userAnswer !== undefined && userAnswer === correctIndex) {
        correctAnswers++;
      }
      totalQuestions++;
    });
    score = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
  }

  if (quizScreens.length > 0) {
    await trackAction('quiz', {
      quizScore: correctAnswers,
      totalQuestions: totalQuestions,
      quizAnswers: savedAnswers
    });
  }

  const isPreviewMode = user && module && user._id === module.vendorId;

  if (isPreviewMode) {
    setCompleted(true);
    setIsReviewMode(true);
    setProgress(prev => ({ ...prev, completed: true, score: score, quizAnswers: savedAnswers }));
    setShowResults(true);
    toast.success('🎉 Preview complete! Credits would be awarded to readers.');
    
    // ✅ Refresh progress for preview mode too
    await fetchProgress();
    window.dispatchEvent(new Event('curiousStatusChanged'));
    return;
  }

  await trackAction('complete');

  setCompleted(true);
  setIsReviewMode(true);
  setProgress(prev => ({ ...prev, completed: true, score: score, quizAnswers: savedAnswers }));
  setShowResults(true);

  // ✅ Set Curious status for anonymous users
  if (!user) {
    localStorage.setItem('curious_status', 'active');
    toast.success('🔍 You are now a Curious explorer!');
    toast.success('📚 Complete more courses or register to save your progress.');
  }

  if (module.reward && module.reward.type === 'credit' && module.reward.creditAmount > 0) {
    toast.success(`🎉 You earned ${module.reward.creditAmount} credits!`);
  }

  // ✅ Refresh progress for ALL users (anonymous AND logged-in)
  await fetchProgress();
  window.dispatchEvent(new Event('curiousStatusChanged'));
};

  const handleClaimReward = () => {
    if (!user) {
      navigate(`/register?redirect=/learn/${slug}`);
      return;
    }
    navigate('/products');
  };

  const handleShare = async (method) => {
    setSharing(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`/api/education-modules/${module._id}/share`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          shareMethod: method,
          recipientEmail: method === 'email' ? shareEmail : null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setShareUrl(data.shareUrl);
        if (method === 'email') {
          setShareSent(true);
          toast.success('Email sent!');
        } else {
          navigator.clipboard.writeText(data.shareUrl);
          toast.success('Link copied to clipboard!');
        }
      } else {
        toast.error('Failed to share');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Failed to share');
    } finally {
      setSharing(false);
    }
  };

  const getDashboardPath = () => {
    if (!user) return '/';
    const role = user.role;
    const map = {
      admin: '/admin/education',
      educator: '/educator-dashboard',
      producer: '/producer-dashboard?tab=education',
      hub: '/hub-dashboard?tab=education',
      buyer: '/buyer/dashboard'
    };
    return map[role] || '/';
  };

  const getNextModule = () => {
    if (courseInfo && courseInfo.modules) {
      const currentIndex = courseInfo.modules.findIndex(m => m._id === module._id);
      if (currentIndex !== -1 && currentIndex < courseInfo.modules.length - 1) {
        return courseInfo.modules[currentIndex + 1];
      }
    }
    if (module && module.nextModuleSlug) {
      return { slug: module.nextModuleSlug, title: 'Next Module' };
    }
    return null;
  };

  const nextModule = getNextModule();
  const allModulesComplete = courseInfo && courseProgress && courseProgress.completedModules === courseInfo.totalModules;

  // ============ RENDER SECTION ============

  if (isUserLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading module...</p>
        </div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-600">Module not found</h2>
        <p className="text-gray-500 mt-2">The module you're looking for doesn't exist or has expired.</p>
      </div>
    );
  }

// RESULTS SCREEN
if (showResults || completed) {
  const nextModule = getNextModule();
  const allModulesComplete = courseInfo && courseProgress && courseProgress.completedModules === courseInfo.totalModules;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-bold mb-2">
            {allModulesComplete ? '🎊 Course Complete!' : isReviewMode ? '📚 Module Review' : 'Module Complete!'}
          </h2>
          <p className="text-gray-600">
            {allModulesComplete
              ? `You've finished all ${courseInfo.totalModules} modules in ${courseInfo.title}!`
              : isReviewMode
                ? `Reviewing ${module.title} - you already completed this module.`
                : `Great job learning about ${module.title}`
            }
          </p>
        </div>

        {/* Score */}
        {progress && progress.score !== undefined && (
          <div className={`rounded-lg p-4 mb-6 text-center ${
            progress.score >= 80 ? 'bg-green-50 border border-green-200' :
            progress.score >= 60 ? 'bg-yellow-50 border border-yellow-200' :
            'bg-red-50 border border-red-200'
          }`}>
            <p className={`font-medium ${
              progress.score >= 80 ? 'text-green-700' :
              progress.score >= 60 ? 'text-yellow-700' :
              'text-red-700'
            }`}>
              📊 Your Score: {progress.score}%
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {progress.score >= 80 ? 'Excellent understanding!' :
               progress.score >= 60 ? 'Good understanding - review the material to improve.' :
               'Review the content below and try to understand the concepts better.'}
            </p>
          </div>
        )}

        {/* ============================================================ */}
        {/* SECTION 1: CREDITS & STATUS (Priority for Curious users)      */}
        {/* ============================================================ */}
        {module.reward && module.reward.type === 'credit' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 text-center">
            {module.reward.creditAmount > 0 ? (
              <>
                <p className="text-green-700 text-lg font-medium">
                  🎁 You earned {module.reward.creditAmount} credits!
                </p>
                {user ? (
                  <p className="text-sm text-green-600 mt-1">Credits have been added to your account!</p>
                ) : (
                  <p className="text-sm text-green-600 mt-1">Your credits are waiting. Claim them below!</p>
                )}
              </>
            ) : (
              <>
                <p className="text-green-700 text-lg font-medium">🌟 You've completed a module!</p>
                <p className="text-sm text-green-600 mt-1">Complete more modules to earn credits.</p>
              </>
            )}
          </div>
        )}

        {/* Curious+ Status Display */}
        {!user && (
          <div className={`rounded-lg p-4 mb-6 ${
            localStorage.getItem('curious_email') 
              ? 'bg-purple-50 border border-purple-200' 
              : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`font-medium ${
                  localStorage.getItem('curious_email') 
                    ? 'text-purple-800' 
                    : 'text-yellow-800'
                }`}>
                  {localStorage.getItem('curious_email') ? '📧 Curious+ Explorer' : '🔍 Curious Explorer'}
                </p>
                <p className="text-sm text-gray-600">
                  {localStorage.getItem('curious_email') 
                    ? 'Your progress is saved to your email!' 
                    : 'You\'ve completed a module and earned credits!'}
                </p>
              </div>
              <div className="text-right">
                {module.reward?.creditAmount > 0 && (
                  <p className="text-lg font-bold text-green-600">+{module.reward.creditAmount} credits</p>
                )}
                <p className="text-sm text-gray-600">📚 {modulesCompleted + 1} modules completed</p>
              </div>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* SECTION 2: CALL TO ACTION (For Curious users)                 */}
        {/* ============================================================ */}
        {!user && (
          <div className="space-y-4 mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-700 font-medium text-center">
              What would you like to do next?
            </p>

            {/* Option 1: Register */}
            <button
              onClick={handleClaimReward}
              className="w-full p-4 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition text-left flex items-center gap-3"
            >
              <span className="text-xl">1️⃣</span>
              <div>
                <div className="font-semibold">
                  {module.reward?.creditAmount > 0 
                    ? 'Sign Up & Claim Your Credits' 
                    : 'Sign Up & Start Your Journey'}
                </div>
                <div className="text-sm text-green-100">
                  {module.reward?.creditAmount > 0 
                    ? `Create an account and claim ${module.reward.creditAmount} credits!` 
                    : 'Unlock future opportunities and exclusive benefits'}
                </div>
              </div>
            </button>

            {/* Option 2: Email */}
            <div className="w-full p-4 bg-white border-2 border-gray-200 rounded-lg">
              <div className="flex items-start gap-3">
                <span className="text-xl">2️⃣</span>
                <div className="flex-1">
                  <div className="font-semibold text-gray-700">
                    {module.reward?.creditAmount > 0 
                      ? 'Email Me My Credit Details' 
                      : 'Email Me My Progress'}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {module.reward?.creditAmount > 0 
                      ? 'Enter your email and we\'ll send a link to claim your credits' 
                      : 'Save your progress and receive updates on new opportunities'}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={emailForReminder}
                      onChange={(e) => setEmailForReminder(e.target.value)}
                      className="flex-1 border rounded-lg px-3 py-2 text-sm"
                    />
                    <button
                      onClick={handleSendReminder}
                      disabled={!emailForReminder || reminderSent}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                      {reminderSent ? 'Sent!' : 'Send'}
                    </button>
                  </div>
                  {reminderSent && (
                    <p className="text-green-600 text-sm mt-2">✅ Check your email!</p>
                  )}
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-400 text-center mt-2">
              {module.reward?.creditAmount > 0 
                ? '⏰ Your credits will expire in 30 days if not claimed.' 
                : '🔒 Your progress is saved. Come back anytime.'}
            </p>
          </div>
        )}

        {/* ============================================================ */}
        {/* SECTION 3: SECONDARY OPTIONS (Review, Next, Browse, Share)    */}
        {/* ============================================================ */}
        <div className="border-t border-gray-200 pt-4 mt-4">
          {!user && (
            <p className="text-xs text-gray-400 text-center mb-3">— Or explore more —</p>
          )}

          {/* Review Button */}
          <button
            onClick={() => {
              setIsReviewMode(true);
              setCurrentScreen(1);
              setShowResults(false);
              setCompleted(false);
              toast.success('📚 Review mode enabled - quiz is locked');
            }}
            className="w-full p-4 bg-blue-50 border-2 border-blue-200 text-blue-700 font-semibold rounded-lg hover:bg-blue-100 transition text-left flex items-center gap-3"
          >
            <span className="text-xl">📖</span>
            <div>
              <div className="font-semibold">Review This Module</div>
              <div className="text-sm text-blue-600">Go through the content again with quiz locked</div>
            </div>
          </button>

          {/* Next Module */}
          {nextModule && !allModulesComplete && (
            <button
              onClick={() => {
                setCompleted(false);
                setCurrentScreen(0);
                setQuizAnswers({});
                setShowResults(false);
                setIsReviewMode(false);
                navigate(`/learn/${nextModule.slug}`);
              }}
              className="w-full p-4 bg-purple-50 border-2 border-purple-200 text-purple-700 font-semibold rounded-lg hover:bg-purple-100 transition text-left flex items-center gap-3 mt-3"
            >
              <span className="text-2xl">➡️</span>
              <div>
                <div className="font-semibold">Next Module: {nextModule.title}</div>
                <div className="text-sm text-purple-600">Continue your learning journey</div>
              </div>
            </button>
          )}

          {/* All Modules Complete */}
          {allModulesComplete && (
            <button
              onClick={() => navigate(getDashboardPath())}
              className="w-full p-4 bg-green-50 border-2 border-green-200 text-green-700 font-semibold rounded-lg hover:bg-green-100 transition text-left flex items-center gap-3 mt-3"
            >
              <span className="text-xl">🏆</span>
              <div>
                <div className="font-semibold">View Course Results</div>
                <div className="text-sm text-green-600">See your overall course results</div>
              </div>
            </button>
          )}

          {/* Browse More */}
          <button
            onClick={() => navigate('/learn')}
            className="w-full p-4 bg-blue-50 border-2 border-blue-200 text-blue-700 font-semibold rounded-lg hover:bg-blue-100 transition text-left flex items-center gap-3 mt-3"
          >
            <span className="text-xl">📚</span>
            <div>
              <div className="font-semibold">Browse More Modules</div>
              <div className="text-sm text-blue-600">Explore other educational content</div>
            </div>
          </button>

          {/* Share */}
          <button
            onClick={() => setShowShareModal(true)}
            className="w-full p-4 bg-purple-50 border-2 border-purple-200 text-purple-700 font-semibold rounded-lg hover:bg-purple-100 transition text-left flex items-center gap-3 mt-3"
          >
            <span className="text-xl">📤</span>
            <div>
              <div className="font-semibold">Share This Module</div>
              <div className="text-sm text-purple-600">Send to a friend who might be interested</div>
            </div>
          </button>
        </div>

        {/* ============================================================ */}
        {/* SECTION 4: LOGGED IN USER (already had this)                  */}
        {/* ============================================================ */}
        {user && (
          <div className="text-center mt-4 pt-4 border-t">
            <button
              onClick={() => navigate('/products')}
              className="px-8 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition"
            >
              {user?.isGuest ? 'Continue Browsing →' : 'Start Shopping →'}
            </button>
          </div>
        )}
      </div>

        {/* Share Modal */}
        {showShareModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">Share This Module</h3>
                <button
                  onClick={() => {
                    setShowShareModal(false);
                    setShareSent(false);
                    setShareEmail('');
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Share "{module.title}" with someone who would enjoy it.
              </p>

              {shareSent ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <p className="text-green-700 font-medium">✅ Email sent!</p>
                  <p className="text-sm text-green-600 mt-1">
                    Your friend will receive a link to this module.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="shareMethod"
                        value="email"
                        checked={shareMethod === 'email'}
                        onChange={() => setShareMethod('email')}
                        className="w-4 h-4"
                      />
                      <div>
                        <div className="font-medium">Email</div>
                        <div className="text-sm text-gray-500">Send directly to a friend's inbox</div>
                      </div>
                    </label>

                    <label className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="shareMethod"
                        value="link"
                        checked={shareMethod === 'link'}
                        onChange={() => setShareMethod('link')}
                        className="w-4 h-4"
                      />
                      <div>
                        <div className="font-medium">Link</div>
                        <div className="text-sm text-gray-500">Copy a shareable link to send anywhere</div>
                      </div>
                    </label>
                  </div>

                  {shareMethod === 'email' && (
                    <div className="flex gap-2">
                      <input
                        type="email"
                        placeholder="Friend's email address"
                        value={shareEmail}
                        onChange={(e) => setShareEmail(e.target.value)}
                        className="flex-1 border rounded-lg px-3 py-2 text-sm"
                      />
                      <button
                        onClick={() => handleShare('email')}
                        disabled={!shareEmail || sharing}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                      >
                        {sharing ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  )}

                  {shareMethod === 'link' && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
                        <span className="text-sm text-gray-600 truncate flex-1">
                          {shareUrl || 'Generating link...'}
                        </span>
                        <button
                          onClick={() => {
                            if (shareUrl) {
                              navigator.clipboard.writeText(shareUrl);
                              toast.success('Link copied!');
                            } else {
                              generateShareLink();
                            }
                          }}
                          disabled={sharing}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          {shareUrl ? '📋 Copy' : '⏳ Generate'}
                        </button>
                      </div>
                      {shareUrl && (
                        <p className="text-xs text-gray-400">
                          Share this link via text, WhatsApp, or any messaging app
                        </p>
                      )}
                    </div>
                  )}

                  <p className="text-xs text-gray-400 mt-2 border-t pt-2">
                    💡 We'll track when someone signs up through your shared link
                  </p>
                </div>
              )}

              <button
                onClick={() => {
                  setShowShareModal(false);
                  setShareSent(false);
                  setShareEmail('');
                }}
                className="mt-4 text-sm text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // WELCOME SCREEN
  if (currentScreen === 0) {
    const screens = module.screens || [];
    const totalScreens = screens.length;
    const isOwner = user && module && user._id === module.vendorId;

    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={() => {
              if (isOwner) {
                navigate(getDashboardPath());
              } else {
                window.history.back();
              }
            }}
            className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1"
          >
            ← Back
          </button>
          {isOwner && (
            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Preview Mode</span>
          )}
          {isReviewMode && (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">📚 Review Mode</span>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <h1 className="text-3xl font-bold mb-4">{module.title || 'Module'}</h1>
          <p className="text-gray-600 mb-6">{module.description || ''}</p>

{/* Curious Status Banner on Welcome Screen */}
{!user && isCurious && (
  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
    <p className="text-yellow-800 font-medium">🔍 You're a Curious Explorer!</p>
    <p className="text-yellow-700 text-sm">
      You've started your journey. Complete modules to earn credits!
    </p>
  </div>
)}

          {courseInfo && courseProgress && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-blue-700">Course: {courseInfo.title}</span>
                <span className="text-blue-600">{courseProgress.completedModules || 0}/{courseInfo.totalModules || 0} modules complete</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${courseProgress.percentage || 0}%` }}
                />
              </div>
            </div>
          )}

          {module.reward && module.reward.type === 'credit' && module.reward.creditAmount > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-700 font-medium">
                {isReviewMode
                  ? `✅ You earned ${module.reward.creditAmount} credits when you completed this module!`
                  : `Complete this module and earn ${module.reward.creditAmount} credits!`
                }
              </p>
            </div>
          )}

          {isReviewMode && progress && progress.score !== undefined && (
            <div className={`rounded-lg p-4 mb-6 ${
              progress.score >= 80 ? 'bg-green-50 border border-green-200' :
              progress.score >= 60 ? 'bg-yellow-50 border border-yellow-200' :
              'bg-red-50 border border-red-200'
            }`}>
              <p className={`font-medium ${
                progress.score >= 80 ? 'text-green-700' :
                progress.score >= 60 ? 'text-yellow-700' :
                'text-red-700'
              }`}>
                📊 Your Previous Score: {progress.score}%
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {progress.score >= 80 ? 'Great job!' :
                 progress.score >= 60 ? 'Good effort - review the material to improve!' :
                 'Review the content below and try to understand the concepts better.'}
              </p>
            </div>
          )}

          <div className="flex justify-center gap-2 text-sm text-gray-500 mb-6">
            <span>📚 {totalScreens} screens</span>
            <span>&bull;</span>
            <span>⏱️ ~{Math.max(1, Math.round(totalScreens * 1))} minutes</span>
            {module.order && courseInfo && (
              <span>&bull;</span>
            )}
            {module.order && courseInfo && (
              <span>📖 Module {module.order} of {courseInfo.totalModules}</span>
            )}
            {isReviewMode && (
              <span>&bull;</span>
            )}
            {isReviewMode && (
              <span className="text-blue-600">🔒 Review Mode - Quiz locked</span>
            )}
          </div>

          {isReviewMode && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-blue-700 font-medium">📚 You've already completed this module</p>
              <p className="text-sm text-blue-600 mt-1">You can review the content again, or continue to the next module.</p>
            </div>
          )}

          <button
            onClick={handleStart}
            className="px-8 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition"
          >
            {isReviewMode ? '📖 Review Content' : 'Start Learning →'}
          </button>
        </div>
      </div>
    );
  }

  // CONTENT SCREENS
  if (currentScreen > 0) {
    const screens = module.screens || [];
    const totalScreens = screens.length;
    const isOwner = user && module && user._id === module.vendorId;

    const screenIndex = currentScreen - 1;
    const screen = screens[screenIndex];

    if (!screen) {
      if (currentScreen < totalScreens) {
        setCurrentScreen(currentScreen + 1);
      } else if (!isReviewMode) {
        setShowResults(true);
        setShowCompletionOptions(true);
      }
      return null;
    }

    const isQuizScreen = screen.type === 'quiz';
    const isAnswered = isQuizScreen ? quizAnswers[screenIndex] !== undefined : true;
    const canProceed = !isQuizScreen || isAnswered;

    const allQuestionsAnswered = () => {
      if (!isQuizScreen) return true;
      const quizScreens = screens.filter(s => s.type === 'quiz');
      const answeredAll = quizScreens.every((s) => {
        const idx = screens.indexOf(s);
        return quizAnswers[idx] !== undefined;
      });
      return answeredAll;
    };

    const isLastScreen = currentScreen >= totalScreens;
    const canComplete = isLastScreen && allQuestionsAnswered() && !isReviewMode;

    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={() => {
              if (isOwner) {
                navigate(getDashboardPath());
              } else {
                window.history.back();
              }
            }}
            className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1"
          >
            ← Back
          </button>
          {isOwner && (
            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Preview Mode</span>
          )}
          {isReviewMode && (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">📚 Review Mode</span>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="h-1 bg-gray-200">
            <div
              className="h-full bg-green-500 transition-all"
              style={{ width: `${(currentScreen / totalScreens) * 100}%` }}
            />
          </div>

          <div className="p-8">
            <div className="flex justify-between items-center mb-6">
              <span className="text-sm text-gray-500">
                Screen {currentScreen} of {totalScreens}
              </span>
              <span className="text-sm text-gray-500">
                {Math.round((currentScreen / totalScreens) * 100)}%
              </span>
            </div>

            <h2 className="text-2xl font-bold mb-4">{screen.title || `Screen ${currentScreen}`}</h2>

            {screen.type === 'text' && (
              <div
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: screen.content || '' }}
              />
            )}

            {screen.type === 'quiz' && (
              <Quiz
                key={`quiz-${screenIndex}-${module._id}`}
                question={screen.quizQuestion || 'Quiz Question'}
                options={screen.quizOptions || ['Option 1', 'Option 2', 'Option 3', 'Option 4']}
                correctAnswer={screen.quizCorrectAnswer || 0}
                explanation={screen.quizExplanation || ''}
                onAnswer={(index, isCorrect) => {
                  handleQuizAnswer(screenIndex, index);
                }}
                disabled={isReviewMode || completed}
                showResult={isReviewMode || completed || quizAnswers[screenIndex] !== undefined}
                userAnswer={quizAnswers[screenIndex]}
              />
            )}

            <div className="flex justify-between mt-8 pt-4 border-t">
              <button
                onClick={() => handleScreenChange(currentScreen - 1)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={isReviewMode && isQuizScreen && isAnswered}
              >
                ← Previous
              </button>

              {currentScreen < totalScreens ? (
                <button
                  onClick={() => {
                    if (isReviewMode) {
                      setCurrentScreen(currentScreen + 1);
                    } else {
                      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
                      trackAction('time', { timeSpent });
                      trackAction('screen', { screen: currentScreen + 1 });
                      setCurrentScreen(currentScreen + 1);
                    }
                  }}
                  disabled={!canProceed}
                  className={`px-6 py-2 rounded ${
                    canProceed 
                      ? 'bg-green-600 text-white hover:bg-green-700' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Next →
                </button>
              ) : (
                <button
                  onClick={() => {
                    if (isReviewMode) {
                      setShowResults(true);
                    } else {
                      handleComplete();
                    }
                  }}
                  disabled={!canProceed && !isReviewMode}
                  className={`px-6 py-2 rounded ${
                    (canProceed || isReviewMode)
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  View Results
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
